import { useCallback, useMemo, useState, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";
import {
  Drawer,
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  Button,
  Chip,
  MenuItem,
  TextField,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { format, parseISO } from "date-fns";

import {
  openAddAccountAndPayDialog,
  selectProject,
  openPaymentConfirmationDialog,
} from "../../../../redux/slices/projectsSlice";
import {
  clearMessages,
  changeInvoiceData,
  closeInvoiceDrawer,
  setEditingState,
  fetchInvoiceDetail,
  fetchInvoiceAttachments,
} from "../../../../redux/slices/invoicesSlice";
import AddAccountAndPayDialog from "./AddAccountAndPayDialog";
import PaymentConfirmationDialog from "./PaymentConfirmationDialog";

const InvoiceDetailsDrawer = ({ maxWidth }: any) => {
  const { selectedInvoice, changes, invoiceChargeStatus, isEditingState, invoiceAttachments } =
    useAppSelector((state) => state.invoices) as any;

  const { projects } = useAppSelector((state) => state.projects) as any;
  const { user } = useAppSelector((state) => state.login) as any;

  const canPay = user?.role !== "employee";

  const dispatch = useAppDispatch();

  const [tabValue, setTabValue] = useState(0);

  // Fetch full invoice detail and attachments when an invoice is selected
  useEffect(() => {
    if (selectedInvoice?.id) {
      dispatch(fetchInvoiceDetail(selectedInvoice.id));
      dispatch(fetchInvoiceAttachments(selectedInvoice.id));
    }
  }, [dispatch, selectedInvoice?.id]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getFilesForLineItem = (lineItemId: string) => {
    return (invoiceAttachments || []).filter(
      (a: any) => a.lineItemId === lineItemId,
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes == null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const bankAccounts = useMemo(() => {
    return (
      projects?.find(
        (project: any) => project.providerId === selectedInvoice?.customerId
      )?.bankAccounts || []
    );
  }, [projects, selectedInvoice]);

  const handleClose = useCallback(() => {
    dispatch(clearMessages());
    // Clear the selection in the same tick; a delayed close left the drawer
    // showing its empty fallbacks while it animated out.
    dispatch(closeInvoiceDrawer());
  }, [dispatch]);

  const handleAddBankAccountAndPay = useCallback(() => {
    dispatch(openAddAccountAndPayDialog());

    const project = projects.find(
      (project: any) => project.id === selectedInvoice?.projectId
    );

    if (project) {
      dispatch(selectProject(project));
    }
  }, [dispatch, projects, selectedInvoice]);

  const handleChangeAccount = useCallback(
    (event: any) => {
      const { name, value } = event.target;

      dispatch(
        changeInvoiceData({
          name,
          value,
        })
      );
    },
    [dispatch]
  );

  const handleChangeAmount = useCallback(
    (event: any) => {
      const { name, value } = event.target;

      let cleaned = value.replace(/[^\d.]/g, "");

      cleaned = cleaned.replace(/(\..*)\./g, "$1");

      if (cleaned.includes(".")) {
        const [intPart, decPart] = cleaned.split(".");
        cleaned = intPart + "." + (decPart ? decPart.slice(0, 2) : "");
      }

      if (
        cleaned.startsWith("0") &&
        cleaned.length > 1 &&
        !cleaned.startsWith("0.")
      ) {
        cleaned = cleaned.replace(/^0+/, "") || "0";
      }

      const amount = cleaned === "" ? 0 : cleaned;
      dispatch(
        changeInvoiceData({
          name,
          value: amount,
        })
      );
    },
    [dispatch]
  );

  const handleOpenConfirmationDialog = useCallback(() => {
    dispatch(openPaymentConfirmationDialog());
  }, [dispatch]);

  const status = {
    0: "Paid in full",
    1: "Open",
    2: "Partial payment",
    4: "Scheduled",
  };

  const processing = !!selectedInvoice?.paymentProcessing;
  const paymentStatus = processing
    ? "Processing"
    : (status as any)[selectedInvoice?.paymentStatus] || "Unknown";

  const color = {
    0: "success",
    1: "warning",
    2: "info",
    4: "secondary",
  };

  const paymentStatusColor = processing
    ? "info"
    : (color as any)[selectedInvoice?.paymentStatus] || "default";

  const resetAmount = useCallback(() => {
    dispatch(setEditingState(false));

    dispatch(
      changeInvoiceData({
        name: "amount",
        value: 0,
      })
    );
  }, [dispatch]);

  const handleEditingState = useCallback(() => {
    dispatch(setEditingState(true));
  }, [dispatch]);

  const amountLeft = useMemo(
    () =>
      (selectedInvoice?.amountDue ?? 0) - (selectedInvoice?.scheduledAmount ?? 0),
    [selectedInvoice]
  );

  // Blank transport rows and zero-value placeholders are not line items the
  // client needs to read.
  const lineItems = useMemo(
    () =>
      (selectedInvoice?.invoiceLineItems || []).filter((item: any) => {
        const description = String(item?.description || "").trim();
        return (
          description !== "" ||
          Number(item?.quantity || 0) !== 0 ||
          Number(item?.price || 0) !== 0 ||
          Number(item?.amount || 0) !== 0
        );
      }),
    [selectedInvoice]
  );

  return (
    <Drawer
      open={!!selectedInvoice}
      onClose={handleClose}
      sx={{
        "& .MuiBackdrop-root": {
          backdropFilter: "blur(10px)",
          backgroundColor: "rgba(255, 255, 255, 0.4)",
        },
        "& .MuiDrawer-paper": {
          mt: 7,
          height: "calc(100% - 56px)",
          borderRadius: 0, // Dark theme shadow
          backdropFilter: "blur(10px)",
          width: "100%",
          maxWidth: `${maxWidth ? maxWidth : 570}px`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      anchor="right"
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid var(--outline-variant, #dcdcdc)",
        }}
      >
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Invoice {selectedInvoice?.invoiceNumber} Details
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: { xs: 2.5, sm: 3 } }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="invoice details tabs">
          <Tab label="Info" />
          <Tab label="Line Items" disabled={!lineItems.length} />
        </Tabs>
      </Box>
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          flex: 1,
          overflowY: "auto",
        }}
      >
        {tabValue === 0 && (
          <Grid container rowSpacing={1.75}>
            <Grid size={24}>
              <Typography variant="subtitle1">Project Name:</Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontFamily: "Brandon_Grotesque_medium" }}>
                {selectedInvoice?.projectName || "N/A"}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle1">Invoice Date:</Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontFamily: "Brandon_Grotesque_medium" }}>
                {selectedInvoice?.invoiceDate
                  ? format(parseISO(selectedInvoice.invoiceDate), "MM/dd/yyyy")
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle1">Due Date:</Typography>
            </Grid>
            <Grid size={12}>
              <Typography sx={{ fontFamily: "Brandon_Grotesque_medium" }}>
                {selectedInvoice?.dueDate
                  ? format(parseISO(selectedInvoice.dueDate), "MM/dd/yyyy")
                  : "N/A"}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle1">Payment Status:</Typography>
            </Grid>
            <Grid size={12}>
              <Chip size="small" label={paymentStatus} color={paymentStatusColor} />
            </Grid>
            {processing && (
              <Grid size={12}>
                <Alert severity="info" sx={{ borderRadius: 0 }}>
                  This payment is clearing through the ACH network and is
                  expected to settle in a few business days. The invoice will
                  show as paid once it settles.
                </Alert>
              </Grid>
            )}
            {selectedInvoice?.description ? (
              <>
                <Grid size={12}>
                  <Typography variant="subtitle1">Description:</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography>{selectedInvoice?.description}</Typography>
                </Grid>
              </>
            ) : null}

            <Grid size={12}>
              <Alert icon={false} severity="info" variant="outlined" sx={{ borderRadius: 0 }}>
                <Grid container rowSpacing={1}>
                  <Grid size={12}>
                    <Typography sx={{ fontFamily: "Brandon_Grotesque_medium" }}>Amount:</Typography>
                  </Grid>
                  <Grid sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }} size={12}>
                    {isEditingState ? (
                      <TextField
                        type="number"
                        value={changes?.amount || ""}
                        onChange={handleChangeAmount}
                        onWheel={(e) => { (e.target as any).blur(); }}
                        placeholder={String(amountLeft || "0.00")}
                        helperText={isEditingState && changes?.amount > amountLeft ? "Amount can not exceed amount left to pay" : ""}
                        name="amount"
                        size="small"
                        required={isEditingState}
                        sx={{ "& .MuiInputBase-root": { borderRadius: 0, "& .MuiInputBase-input": { padding: "3.5px 14px" } } }}
                      />
                    ) : (
                      <Typography>
                        {selectedInvoice?.amount?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                      </Typography>
                    )}
                    {isEditingState ? (
                      <IconButton onClick={resetAmount} size="small" sx={{ color: "text.secondary" }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <IconButton onClick={handleEditingState} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Grid>
                  <Grid size={12} sx={{ pl: 1 }}>
                    <Typography sx={{ lineHeight: "1" }}>Amount Due:</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography sx={{ lineHeight: "1" }}>
                      {selectedInvoice?.amountDue?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                    </Typography>
                  </Grid>
                  <Grid size={12} sx={{ pl: 1 }}>
                    <Typography sx={{ lineHeight: "1" }}>Scheduled Amount:</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography sx={{ lineHeight: "1" }}>
                      {selectedInvoice?.scheduledAmount?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                    </Typography>
                  </Grid>
                  <Grid size={12} sx={{ pl: 1 }}>
                    <Typography sx={{ lineHeight: "1", fontFamily: "Brandon_Grotesque_medium" }}>Amount Left:</Typography>
                  </Grid>
                  <Grid size={12}>
                    <Typography sx={{ lineHeight: "1" }}>
                      {amountLeft?.toLocaleString("en-US", { style: "currency", currency: "USD" })}
                    </Typography>
                  </Grid>
                </Grid>
              </Alert>
            </Grid>

            {bankAccounts.length > 0 ? (
              <>
                {canPay && (
                  <>
                    <Grid size={12}>
                      <Typography variant="subtitle1">Select Account:</Typography>
                    </Grid>
                    <Grid size={12}>
                      <TextField
                        sx={{ "& .MuiInputBase-root": { borderRadius: 0 }, "& .MuiSelect-select": { py: "4.5px" } }}
                        fullWidth required size="small"
                        onChange={handleChangeAccount}
                        name="accountId"
                        value={changes?.accountId || ""}
                        select
                      >
                        {bankAccounts?.map((account: any) => (
                          <MenuItem key={account.accountID} value={account.accountID}>
                            {account.accountName} - {account.accountNumber}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </>
                )}
              </>
            ) : (
              <Grid size={12}>
                {canPay && !processing && (
                  <Alert sx={{ borderRadius: 0 }} severity="warning">
                    <Box>
                      <Typography variant="body2">
                        No bank account linked to this Project. Please add a bank account to be able to make payments.
                      </Typography>
                      <Button variant="outlined" color="primary" fullWidth onClick={handleAddBankAccountAndPay}
                        sx={{ mt: 1, borderRadius: 0, p: "2.75px 15px", }}>
                        Add Bank Account
                      </Button>
                    </Box>
                  </Alert>
                )}
              </Grid>
            )}
            {canPay && (
              <Grid size={12}>
                <Button
                  sx={{ mt: 2, borderRadius: 0, p: "2.75px 15px", }}
                  onClick={handleOpenConfirmationDialog}
                  fullWidth color="success" variant="contained"
                  disabled={
                    invoiceChargeStatus === "loading" ||
                    bankAccounts.length === 0 ||
                    !changes?.accountId ||
                    String(selectedInvoice?.paymentStatus) === "0" ||
                    processing ||
                    amountLeft <= 0 ||
                    (isEditingState && changes?.amount > amountLeft)
                  }
                >
                  Pay now
                </Button>
              </Grid>
            )}
          </Grid>
        )}

        {tabValue === 1 && (
          <Grid container rowSpacing={1.75}>
            <Grid size={24}>
              <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Line Items</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Files</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item: any, index: number) => {
                      const lineItemId = item.id || String(index);
                      const lineFiles = getFilesForLineItem(lineItemId);
                      return (
                        <TableRow key={index}>
                          <TableCell>{item.description || "—"}</TableCell>
                          <TableCell align="right">
                            {Number(item.quantity || 0) !== 0
                              ? Number(item.quantity).toLocaleString("en-US")
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {Number(item.price || 0) !== 0
                              ? Number(item.price).toLocaleString("en-US", { style: "currency", currency: "USD" })
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {Number(item.amount || 0) !== 0
                              ? Number(item.amount).toLocaleString("en-US", { style: "currency", currency: "USD" })
                              : "—"}
                          </TableCell>
                          <TableCell align="center">
                            {lineFiles.length > 0 && (
                              <Chip label={lineFiles.length} size="small" color="primary" variant="outlined" />
                            )}
                            {lineFiles.length > 0 && (
                              <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
                                {lineFiles.map((file: any) => (
                                  <Box key={file.id} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <Link
                                      href={file.accessUrl || file.fileUrl}
                                      target="_blank" rel="noopener" underline="hover"
                                      sx={{
                                        fontSize: "0.75rem", flexGrow: 1,
                                        overflow: "hidden", textOverflow: "ellipsis",
                                        whiteSpace: "nowrap", maxWidth: 120, display: "inline-block",
                                      }}
                                    >
                                      {file.fileName}
                                    </Link>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                                      {formatFileSize(file.fileSize)}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </Box>
      {canPay && <AddAccountAndPayDialog />}
      {canPay && <PaymentConfirmationDialog />}
    </Drawer>
  );
};

export default InvoiceDetailsDrawer;
